import type { Game } from "../../api/campaigns";
import { SCRATCH_CARD_FAMILY_DESCRIPTION } from "./scratchProductCopy";

/**
 * Ordered game families for the catalog. Add new families (e.g. scratch cards)
 * and their game keys here as games are registered — no UI change required.
 */
export interface GameCatalogFamily {
  id: string;
  title: string;
  /** Optional blurb under the section title */
  description?: string;
  /** Backend `Game.key` values belonging to this family */
  gameKeys: readonly string[];
}

export const CATALOG_GAME_FAMILIES: readonly GameCatalogFamily[] = [
  {
    id: "wheels-of-fortune",
    title: "Wheels of Fortune",
    description:
      "Pick a wheel template (lead capture or instant discount), then configure segments and rewards on the campaign.",
    gameKeys: ["wheel_of_fortune"],
  },
  {
    id: "scratch-cards",
    title: "Scratch Cards",
    description: SCRATCH_CARD_FAMILY_DESCRIPTION,
    gameKeys: ["scratch_card"],
  },
];

export interface CatalogSectionGroup {
  family: GameCatalogFamily;
  games: Game[];
}

/**
 * Assign each game to at most one family (first matching `gameKeys`).
 * Unmapped games are returned separately so nothing disappears from the API.
 */
export function groupGamesByCatalogFamily(games: Game[]): {
  sections: CatalogSectionGroup[];
  unmappedGames: Game[];
} {
  const assignedIds = new Set<string>();
  const sections: CatalogSectionGroup[] = [];

  for (const family of CATALOG_GAME_FAMILIES) {
    const inFamily = games.filter((g) => family.gameKeys.includes(g.key));
    inFamily.forEach((g) => assignedIds.add(g.id));
    if (inFamily.length > 0) {
      sections.push({ family, games: inFamily });
    }
  }

  const unmappedGames = games.filter((g) => !assignedIds.has(g.id));

  return { sections, unmappedGames };
}
